import {
  SetStateAction,
  Dispatch,
  ChangeEvent,
  useEffect,
  useState,
} from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubFilter,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubPaper,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { debounce } from 'debounce';
import { Progress } from '@backstage/core-components';
import { Device } from '../types/InsightsTypes';
import EnvironmentFilter from './EnvironmentFilter';

const SearchDevicesQuery = gql`
  query FindDevicesByApp(
    $applicationId: Int!
    $filter: ResourceCatalogDeviceFilter
  ) {
    resources {
      application(applicationId: $applicationId) {
        devicesPaged(filter: $filter) {
          devices {
            ipAddress
            hostname
            environment
            type
            tier
            href
            osInfo {
              make
              name
              edition
              release
              version
            }
          }
        }
      }
    }
  }
`;

type Props = {
  setSearchResults: Dispatch<SetStateAction<Device[]>>;
  appId?: number;
  searchResults: Device[];
  selectedEnvs: string[];
  handleEnvChange: (env: string) => void;
  selectedTiers: string[];
  handleTierChange: (status: string) => void;
  deviceList: Device[];
  multiline?: boolean;
};

/**
 * DeviceSearch searches for devices belonging to an application
 * @param props Props of the component
 * @param props.setApps Set applications on parent to search results
 * @param props.multiline Whether or not to allow multiline input
 */
const DeviceSearch = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [getDevices, { loading, error }] = useLazyQuery(SearchDevicesQuery, {
    errorPolicy: 'all',
  });

  useEffect(() => {
    const tiers = [...new Set(props.deviceList.map(device => device.tier))];
    setSelectedTiers(tiers);
  }, [props.deviceList]);

  const handleSearch = debounce((value: string) => {
    if (value) {
      getDevices({
        variables: {
          applicationId: props?.appId,
          filter: { name: `${value}` },
        },
      }).then(results => {
        props.setSearchResults(
          results?.data?.resources?.application?.devicesPaged?.devices,
        );
      });
    }
    setSearchExecuted(true);
  }, 300);

  const clearSearch = () => {
    setSearchTerm('');
    setSearchExecuted(false);
    props.setSearchResults([]);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value?.length <= 0) {
      clearSearch();
      return;
    }

    if (value.length >= 3 && value.length - searchTerm.length > 0) {
      handleSearch(value);
    }
    setSearchTerm(value);
  };

  const renderAlertMessage = () => {
    const totalCount = props?.searchResults?.length || 0;
    const resultsText = totalCount === 1 ? 'result' : 'results';

    return `Showing ${totalCount} ${resultsText} for "${searchTerm}"`;
  };

  return (
    <>
      <DevHubPaper>
        <DevHubGrid container alignItems="center" spacing={1}>
          <DevHubGrid item xs={12} sm={12} md={6} lg={4}>
            {error ? (
              <DevHubTextField
                error
                label="Error searching for devices."
                helperText="Please try again later"
                variant="outlined"
                value={searchTerm}
                size="small"
                onChange={onChange}
                InputLabelProps={{
                  style: {
                    transform: 'translate(0, -50%)',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <DevHubInputAdornment position="end">
                      <DevHubIconButton onClick={clearSearch}>
                        <DevHubDynamicIcon iconName="Clear" />
                      </DevHubIconButton>
                    </DevHubInputAdornment>
                  ),
                }}
              />
            ) : (
              <DevHubTextField
                id="device-search"
                label="Search By Hostname"
                margin="normal"
                variant="outlined"
                noMargin
                multiline={props.multiline}
                onChange={onChange}
                value={searchTerm}
                size="small"
                InputProps={{
                  endAdornment: (
                    <DevHubInputAdornment position="end">
                      <DevHubIconButton onClick={clearSearch}>
                        <DevHubDynamicIcon iconName="Clear" />
                      </DevHubIconButton>
                    </DevHubInputAdornment>
                  ),
                }}
              />
            )}
          </DevHubGrid>
          <DevHubGrid item>
            <EnvironmentFilter
              deviceList={
                props?.searchResults?.length > 0
                  ? props?.searchResults
                  : props?.deviceList
              }
              handleEnvChange={props.handleEnvChange}
              selectedEnvs={props.selectedEnvs}
            />
          </DevHubGrid>
          <DevHubGrid item>
            <DevHubFilter
              list={selectedTiers}
              title="Tier"
              selectedItems={props.selectedTiers}
              handleChange={props.handleTierChange}
            />
          </DevHubGrid>
          {props.selectedEnvs.length > 0 &&
            props.selectedEnvs.map((env: string) => (
              <DevHubGrid item key={`env-${env}`}>
                <DevHubChip
                  noMargins
                  label={env}
                  onDelete={() => props.handleEnvChange(env)}
                />
              </DevHubGrid>
            ))}
          {props.selectedTiers.length > 0 &&
            props.selectedTiers.map((tier: string) => (
              <DevHubGrid item key={`tier-${tier}`}>
                <DevHubChip
                  noMargins
                  label={tier}
                  onDelete={() => props.handleTierChange(tier)}
                />
              </DevHubGrid>
            ))}
        </DevHubGrid>
      </DevHubPaper>
      {loading && (
        <DevHubBox mt={1}>
          <Progress />
        </DevHubBox>
      )}
      {error && (
        <DevHubBox mt={1}>
          <DevHubAlert variant="filled" severity="error">
            Error loading data
          </DevHubAlert>
        </DevHubBox>
      )}
      {!loading &&
        searchExecuted &&
        (props?.searchResults?.length === 0 ? (
          <DevHubBox mt={1}>
            <DevHubAlert variant="outlined" severity="error">
              No results found for &quot;{searchTerm}&quot;
            </DevHubAlert>
          </DevHubBox>
        ) : (
          <DevHubBox mt={1}>
            <DevHubAlert variant="outlined" severity="success">
              {renderAlertMessage()}
            </DevHubAlert>
          </DevHubBox>
        ))}
    </>
  );
};

export default DeviceSearch;
